import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { getUserById, updateUser as updateUserApi, createUser as createUserApi } from "../../api/getUserList";
import { occupations } from "../../data/Signup/occupationArray";
import { commonErrorToastMessage, commonSuccessToastMessage } from "../../components/toastify/AllToastifyErrors";
import "./Utility.css";

const PLACEHOLDER_OCCUPATION = "Select Occupation (Required)";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = () => ({
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  occupation: PLACEHOLDER_OCCUPATION,
  otherOccupationText: "",
});

const AddEditUserModal = ({ open, onClose, mode, editingUserId, onSuccess }) => {
  const [form, setForm] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loadUserLoading, setLoadUserLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "add") {
      setForm(emptyForm());
      setFormErrors({});
      return;
    }
    if (mode === "edit" && editingUserId != null) {
      setLoadUserLoading(true);
      setFormErrors({});
      getUserById(editingUserId)
        .then((userData) => {
          const rawOccupation = userData.occupation || PLACEHOLDER_OCCUPATION;
          const isOther =
            rawOccupation === "Other" ||
            rawOccupation.toLowerCase().startsWith("other~");
          const otherText = rawOccupation.toLowerCase().startsWith("other~")
            ? rawOccupation.slice("other~".length)
            : "";
          setForm({
            firstName: userData.firstName ?? userData.name?.split(" ")[0] ?? "",
            lastName: userData.lastName ?? userData.name?.split(" ").slice(1).join(" ") ?? "",
            email: userData.email ?? "",
            password: "",
            occupation: isOther ? "Other" : rawOccupation,
            otherOccupationText: otherText,
          });
        })
        .catch((err) => {
          commonErrorToastMessage(err?.response?.data?.error || "Failed to load user.");
          onClose();
        })
        .finally(() => setLoadUserLoading(false));
    }
  }, [open, mode, editingUserId, onClose]);

  const handleClose = () => {
    setForm(emptyForm());
    setFormErrors({});
    onClose();
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const err = {};
    if (!form.firstName?.trim()) err.firstName = "First name is required.";
    if (!form.lastName?.trim()) err.lastName = "Last name is required.";
    if (!form.email?.trim()) err.email = "Email is required.";
    else if (!EMAIL_REGEX.test(form.email.trim())) err.email = "Please enter a valid email address.";
    if (mode === "add" && !form.password?.trim()) err.password = "Password is required.";
    if (!form.occupation?.trim() || form.occupation === PLACEHOLDER_OCCUPATION) {
      err.occupation = "Please select an occupation.";
    }
    if (form.occupation === "Other" && !form.otherOccupationText?.trim()) {
      err.otherOccupationText = "Please specify your occupation.";
    }
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        occupation:
          form.occupation === "Other"
            ? `other~${form.otherOccupationText.trim()}`
            : form.occupation,
      };
      if (mode === "add") {
        payload.password = form.password;
        await createUserApi(payload);
        commonSuccessToastMessage("User created successfully.");
      } else {
        if (form.password?.trim()) payload.password = form.password;
        await updateUserApi(editingUserId, payload);
        commonSuccessToastMessage("User updated successfully.");
      }
      handleClose();
      onSuccess?.();
    } catch (err) {
      commonErrorToastMessage(
        err?.response?.data?.error ||
          (mode === "add" ? "Failed to create user." : "Failed to update user.")
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#042739",
          color: "white",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        {mode === "add" ? "Add user" : "Edit user"}
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loadUserLoading ? (
          <div className="utility-modal-loading-text">
            Loading user…
          </div>
        ) : (
          <>
            <TextField
              margin="dense"
              label="First name"
              required
              fullWidth
              value={form.firstName}
              onChange={(e) => handleFormChange("firstName", e.target.value)}
              error={!!formErrors.firstName}
              helperText={formErrors.firstName}
              sx={{ mb: 1 }}
            />
            <TextField
              margin="dense"
              label="Last name"
              required
              fullWidth
              value={form.lastName}
              onChange={(e) => handleFormChange("lastName", e.target.value)}
              error={!!formErrors.lastName}
              helperText={formErrors.lastName}
              sx={{ mb: 1 }}
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              required
              fullWidth
              value={form.email}
              onChange={(e) => handleFormChange("email", e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
              sx={{ mb: 1 }}
            />
            {mode === "add" && (
              <TextField
                margin="dense"
                label="Password"
                type="password"
                required
                fullWidth
                value={form.password}
                onChange={(e) => handleFormChange("password", e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password}
                sx={{ mb: 1 }}
              />
            )}
            <FormControl
              fullWidth
              margin="dense"
              required
              error={!!formErrors.occupation}
              sx={{ mb: 1 }}
            >
              <InputLabel id="occupation-label">Occupation</InputLabel>
              <Select
                labelId="occupation-label"
                label="Occupation"
                value={form.occupation}
                onChange={(e) => handleFormChange("occupation", e.target.value)}
              >
                {occupations.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.occupation && (
                <span className="utility-modal-field-error-text">
                  {formErrors.occupation}
                </span>
              )}
            </FormControl>
            {form.occupation === "Other" && (
              <TextField
                margin="dense"
                label="Please specify your occupation"
                required
                fullWidth
                value={form.otherOccupationText}
                onChange={(e) => handleFormChange("otherOccupationText", e.target.value)}
                error={!!formErrors.otherOccupationText}
                helperText={formErrors.otherOccupationText}
                sx={{ mb: 1 }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ color: "#777E8D" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitLoading || loadUserLoading}
          sx={{
            backgroundColor: "#F4833D",
            "&:hover": { backgroundColor: "#e06a21" },
          }}
        >
          {submitLoading ? "Saving…" : mode === "add" ? "Add user" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditUserModal;
